'use client';

import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonClose } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import { Modal } from '@/components/Modal';
import { blurImagePlaceholder } from '@/theme/placeholders';
import { formatCompPoints } from '../../helpers';
import type { BonusShopProduct, BonusShopProductVersion } from '../../types';
import { getErrorContent, getSuccessContent } from './helpers';
import { ScResultModal } from './styled';

interface BonusShopResultModalProps {
  product: BonusShopProduct;
  version?: BonusShopProductVersion;
  balance: number;
  isError?: boolean;
  onAction: () => void;
  onClose: () => void;
}

const STATUS_ICON_SIZE = 96;
const PRODUCT_IMAGE_SIZE = 64;
const CONFETTI_WIDTH = 480;
const CONFETTI_HEIGHT = 217;

export const BonusShopResultModal: FC<BonusShopResultModalProps> = ({
  product,
  version,
  balance,
  isError,
  onAction,
  onClose,
}) => {
  const { t } = useTranslation('bonusShop');
  const content = isError ? getErrorContent(t) : getSuccessContent(product, t);
  const title = version ? `${version.label} ${product.title}` : product.title;

  return (
    <Modal isPortal padding={16}>
      <ScResultModal>
        {!isError && (
          <ImageComponent
            alt=""
            className="confetti"
            height={CONFETTI_HEIGHT}
            src="/bonus-shop/confetti.webp"
            width={CONFETTI_WIDTH}
          />
        )}

        <ButtonClose onClick={onClose} right={12} top={12} />

        <div className="content">
          <ImageComponent
            alt=""
            className="status-icon"
            height={STATUS_ICON_SIZE}
            src={isError ? '/bonus-shop/err.webp' : '/bonus-shop/done.webp'}
            width={STATUS_ICON_SIZE}
          />

          <div className="texts">
            <p className="title">{content.title}</p>
            <p className="subtitle">{content.subtitle}</p>
          </div>

          {content.withAmount && product.amount && (
            <div className="amount-box">
              <p className="amount">{product.amount}</p>
              <p className="amount-note">{t('result.withdrawableCash')}</p>
            </div>
          )}

          {content.withProduct && (
            <div className="product">
              <ImageComponent
                alt={product.title}
                blurDataURL={blurImagePlaceholder}
                className="product-image"
                height={PRODUCT_IMAGE_SIZE}
                placeholder="blur"
                src={product.img?.url || blurImagePlaceholder}
                width={PRODUCT_IMAGE_SIZE}
              />
              {content.productNote ? (
                <p className="product-note">{content.productNote}</p>
              ) : (
                <p className="product-title">{title}</p>
              )}
            </div>
          )}
        </div>

        <div className="actions">
          <Button onClick={onAction} primary wide>
            {content.action}
          </Button>

          <p className="balance">
            {t('result.balance', { balance: formatCompPoints(balance) })}
          </p>
        </div>
      </ScResultModal>
    </Modal>
  );
};
